import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/lib/icons';
import { themeImportErrorMessage } from '@/api/theme-import-history.contract';
import { useThemeImportQuestions } from './use-theme-import-questions';
import { ThemeQuestionDomain } from './ThemeQuestionDomain';

export interface ThemeImportQuestionsDialogProps {
  themeId: string;
  themeTitle: string;
  themeSectorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copy: {
    title: string;
    description: string;
    loading: string;
    empty: string;
    retry: string;
    more: string;
    loadingMore: string;
    count: string;
    error: string;
    colText: string;
    colLanguage: string;
    colDomain: string;
    colOrder: string;
    unknownLanguage: string;
    unknownDomain: string;
    questionDomainSource: string;
    themeDomainSource: string;
    errors: Record<string, string>;
  };
}

export function ThemeImportQuestionsDialog({
  themeId,
  themeTitle,
  themeSectorName,
  open,
  onOpenChange,
  copy,
}: ThemeImportQuestionsDialogProps) {
  const query = useThemeImportQuestions(themeId, open);
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];
  const loadingMore = query.isFetchingNextPage || query.isPlaceholderData;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100%-2rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl border border-border/50 bg-background p-0 shadow-2xl sm:w-full">
        <DialogHeader className="border-b border-border/50 bg-background px-6 py-5 text-left">
          <DialogTitle className="truncate pr-6">{copy.title.replace('{theme}', themeTitle)}</DialogTitle>
          <DialogDescription className="leading-6">{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          {query.isPending && (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Icon name="loader2" className="h-4 w-4 animate-spin" />
              {copy.loading}
            </p>
          )}
          {query.isError && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 p-4 text-sm text-destructive">
              <span>{themeImportErrorMessage(query.error, copy.errors)}</span>
              <button type="button" onClick={() => void query.refetch()} className="font-semibold underline">
                {copy.retry}
              </button>
            </div>
          )}
          {!query.isPending && !query.isError && rows.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">{copy.empty}</p>
          )}

          {rows.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-border/40 bg-background/50">
              <div className="grid grid-cols-[minmax(0,1fr)_0.5fr_0.5fr_0.3fr] gap-x-4 border-b border-border/40 bg-muted/20 px-4 py-2.5 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <span>{copy.colText}</span>
                <span>{copy.colLanguage}</span>
                <span>{copy.colDomain}</span>
                <span className="text-right">{copy.colOrder}</span>
              </div>
              <ul className="divide-y divide-border/40">
                {rows.map((question) => (
                  <li
                    key={question.id}
                    className="grid grid-cols-[minmax(0,1fr)_0.5fr_0.5fr_0.3fr] items-center gap-x-4 px-4 py-3"
                  >
                    <span className="min-w-0 truncate text-sm text-foreground/90" title={question.questionText}>
                      {question.questionText}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {question.language ?? copy.unknownLanguage}
                    </span>
                    <span className="min-w-0 text-xs text-muted-foreground">
                      <ThemeQuestionDomain questionDomain={question.domain} themeSectorName={themeSectorName} copy={copy} />
                    </span>
                    <span className="text-right text-xs tabular-nums text-muted-foreground">
                      {question.order ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {!query.isPending && !query.isError && (
          <div className="flex items-center justify-between border-t border-border/50 bg-background px-6 py-3 text-xs">
            <span className="text-muted-foreground">{copy.count.replace('{count}', String(rows.length))}</span>
            {query.hasNextPage && (
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void query.fetchNextPage()}
                className="inline-flex items-center gap-2 font-semibold disabled:opacity-35"
              >
                {loadingMore && <Icon name="loader2" className="h-3.5 w-3.5 animate-spin" />}
                {loadingMore ? copy.loadingMore : copy.more}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
